import api from './api';
import { debounce } from 'lodash';

/**
 * Service de recherche intelligent avec personnalisation et optimisations
 * Version 2.0 - Intégré avec les nouveaux services backend
 */
class IntelligentSearchService {
  constructor() {
    this.searchCache = new Map();
    this.suggestionCache = new Map();
    this.searchHistory = [];
    this.maxHistorySize = 50;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    
    // 🆕 REGISTRE INTELLIGENT DES RECHERCHES
    this.searchRegistry = new Map(); // Registre pour tracker les recherches uniques
    this.searchCounter = 0; // Compteur global des recherches
    
    // Debounced search pour éviter trop de requêtes
    this.debouncedSearch = debounce(this._performSearch.bind(this), 300);
    this.debouncedSuggestions = debounce(this._getSuggestions.bind(this), 150);
    
    // Analytics tracking
    this.analyticsEnabled = true;
    this.performanceMetrics = {
      searchCount: 0,
      averageResponseTime: 0,
      lastSearchTimestamp: null
    };
  }

  /**
   * Recherche principale avec préférences utilisateur intégrées
   */
  async searchWithPreferences(query, userPreferences = {}, filters = {}, options = {}) {
    const serviceStartTime = Date.now();
    const searchId = `frontend_search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      console.log(`\n� ========== [FRONTEND SERVICE:${searchId}] DÉBUT RECHERCHE INTELLIGENTE ==========`);
      console.log(`�📥 [IntelligentSearchService:${searchId}] PARAMÈTRES D'ENTRÉE:`, {
        query: {
          value: query,
          type: typeof query,
          length: query ? query.length : 0,
          isEmpty: !query || query.trim() === ''
        },
        userPreferences: {
          hasPreferences: !!userPreferences && Object.keys(userPreferences).length > 0,
          keys: userPreferences ? Object.keys(userPreferences) : [],
          search: userPreferences?.search ? {
            hasSearchPrefs: true,
            resultsPerPage: userPreferences.search.resultsPerPage,
            preferredCategories: userPreferences.search.preferredCategories,
            savedLocations: userPreferences.search.savedLocations
          } : null
        },
        filters: {
          hasFilters: !!filters && Object.keys(filters).length > 0,
          keys: filters ? Object.keys(filters) : [],
          criticalFilters: {
            country: filters?.country,
            city: filters?.city,
            cities: filters?.cities,
            categoryId: filters?.categoryId,
            isAvailable: filters?.isAvailable,
            isVerified: filters?.isVerified
          }
        },
        options: {
          hasOptions: !!options && Object.keys(options).length > 0,
          keys: options ? Object.keys(options) : [],
          criticalOptions: {
            offset: options?.offset,
            sortBy: options?.sortBy,
            sortOrder: options?.sortOrder,
            forceRefresh: options?.forceRefresh
          }
        },
        timestamp: new Date().toISOString()
      });
      
      // 🆕 SIMPLIFICATION TOTALE : Plus de logique de préférences complexe
      console.log(`🔧 [IntelligentSearchService:${searchId}] UTILISATION DIRECTE DES FILTRES - PAS DE PRÉFÉRENCES...`);
      
      // Utiliser directement les filtres reçus, sans traitement
      const enhancedFilters = { ...filters };
      
      console.log(`📋 [IntelligentSearchService:${searchId}] FILTRES DIRECTS:`, {
        originalFilters: filters,
        finalFilters: enhancedFilters,
        noTransformation: true,
        directPassthrough: true
      });
      
      // 📅 Si un filtre de date est actif, forcer le bypass du cache local ET backend.
      // Les données de disponibilité changent en temps réel (réservations, annulations).
      // Un résultat mis en cache avant une annulation masquerait des créneaux devenus libres.
      const hasDateFilter = !!(enhancedFilters.startDate || enhancedFilters.endDate);
      if (hasDateFilter && !options.forceRefresh) {
        options = { ...options, forceRefresh: true };
        console.log(`📅 [IntelligentSearchService:${searchId}] Date filter detected → forceRefresh forced (real-time availability)`);
      }

      // Construire les paramètres de recherche
      const searchParams = {
        search: query,
        ...enhancedFilters,
        status: 'active', // Toujours chercher les annonces valides
        limit: options.limit || 20,
        offset: options.offset || 0,
        sortBy: options.sortBy || 'rating',
        sortOrder: options.sortOrder || 'DESC',
        // 📅 Transmettre forceRefresh au backend pour bypasser son cache middleware ET son cache interne
        ...(options.forceRefresh ? { forceRefresh: true } : {})
      };

      // ✅ Vérification des filtres (version optimisée)
      console.log(`� [IntelligentSearchService:${searchId}] FILTRES APPLIQUÉS:`, {
        country: searchParams.country || 'non défini',
        city: searchParams.city || 'non défini', 
        cities: searchParams.cities || [],
        otherFilters: Object.keys(searchParams).filter(k => !['country', 'city', 'cities', 'search', 'status', 'limit', 'offset', 'sortBy', 'sortOrder'].includes(k)).length
      });

      console.log(`🌐 [IntelligentSearchService:${searchId}] PARAMÈTRES FINAUX POUR L'API:`, {
        endpoint: '/search/intelligent',
        method: 'GET',
        searchParams: searchParams,
        parameterAnalysis: {
          totalKeys: Object.keys(searchParams).length,
          textQuery: searchParams.search,
          locationFilters: {
            country: searchParams.country,
            city: searchParams.city,
            cities: searchParams.cities
          },
          businessLogic: {
            categoryId: searchParams.categoryId,
            isAvailable: searchParams.isAvailable,
            isVerified: searchParams.isVerified,
            status: searchParams.status
          },
          sorting: {
            sortBy: searchParams.sortBy,
            sortOrder: searchParams.sortOrder,
            limit: searchParams.limit,
            offset: searchParams.offset
          }
        }
      });

      // 🎯 GESTION INTELLIGENTE DU CACHE AVEC REGISTRE
      const cacheKey = this._generateCacheKey(searchParams);
      const isSearchRegistered = this.searchRegistry.has(cacheKey);
      
      console.log(`🗄️ [IntelligentSearchService:${searchId}] ANALYSE INTELLIGENTE DU CACHE:`, {
        cacheKey: {
          value: cacheKey.substring(0, 80) + '...',
          fullLength: cacheKey.length,
          hash: this._hashString(cacheKey)
        },
        registryStatus: {
          isSearchRegistered: isSearchRegistered,
          registrySize: this.searchRegistry.size,
          searchNumber: isSearchRegistered ? this.searchRegistry.get(cacheKey).searchNumber : 'new'
        },
        cacheStatus: {
          hasEntry: this.searchCache.has(cacheKey),
          cacheSize: this.searchCache.size,
          forceRefresh: options.forceRefresh
        },
        decision: {
          shouldUseCache: this.searchCache.has(cacheKey) && !options.forceRefresh && isSearchRegistered,
          shouldSkipCache: options.forceRefresh || !isSearchRegistered,
          reason: options.forceRefresh ? 'forceRefresh requested' : 
                  !isSearchRegistered ? 'new search pattern' : 
                  'normal cache check'
        }
      });
      
      // 🆕 DÉCISION INTELLIGENTE : Utiliser le cache seulement si la recherche est déjà enregistrée
      if (this.searchCache.has(cacheKey) && !options.forceRefresh && isSearchRegistered) {
        const cached = this.searchCache.get(cacheKey);
        const cacheAge = Date.now() - cached.timestamp;
        
        if (cacheAge < this.cacheTimeout) {
          console.log(`💾 [IntelligentSearchService:${searchId}] CACHE HIT INTELLIGENT - RETOUR RÉSULTATS:`, {
            searchPattern: 'already registered',
            cacheAge: `${cacheAge}ms`,
            cacheTimeout: `${this.cacheTimeout}ms`,
            resultCount: cached.data.listings?.length || 0,
            totalFound: cached.data.total || 0,
            skipApiCall: true,
            registryEntry: this.searchRegistry.get(cacheKey)
          });
          
          // Mettre à jour les métadonnées du registre
          this.searchRegistry.set(cacheKey, {
            ...this.searchRegistry.get(cacheKey),
            lastAccessed: Date.now(),
            accessCount: this.searchRegistry.get(cacheKey).accessCount + 1
          });
          
          console.log(`🏁 ========== [FRONTEND SERVICE:${searchId}] FIN (CACHE INTELLIGENT) ==========\n`);
          return cached.data;
        } else {
          console.log(`⏰ [IntelligentSearchService:${searchId}] CACHE EXPIRÉ - NETTOYAGE:`, {
            cacheAge: `${cacheAge}ms`,
            cacheTimeout: `${this.cacheTimeout}ms`,
            action: 'removing expired entry and registry'
          });
          this.searchCache.delete(cacheKey);
          this.searchRegistry.delete(cacheKey); // Nettoyer aussi le registre
        }
      } else if (!isSearchRegistered) {
        console.log(`🆕 [IntelligentSearchService:${searchId}] NOUVELLE RECHERCHE DÉTECTÉE:`, {
          reason: 'Search pattern not in registry',
          action: 'will bypass cache and register new search',
          cacheKeyExists: this.searchCache.has(cacheKey),
          forceRefresh: options.forceRefresh
        });
      } else if (options.forceRefresh) {
        console.log(`🔄 [IntelligentSearchService:${searchId}] FORCE REFRESH DEMANDÉ:`, {
          reason: 'Manual filter application',
          action: 'bypassing cache deliberately',
          registeredSearch: isSearchRegistered
        });
      }

    console.log(`📤 [IntelligentSearchService:${searchId}] APPEL API BACKEND EN COURS...`);
    
    // 🚨 ANALYSE CRITIQUE: Vérifier les valeurs exactes avant nettoyage
    console.log(`🔍 [ANALYSE CRITIQUE] VALEURS EXACTES SPAIN/BARCELONA:`, {
      country: {
        value: searchParams.country,
        type: typeof searchParams.country,
        isString: typeof searchParams.country === 'string',
        length: searchParams.country?.length,
        isEmpty: searchParams.country === '',
        isNull: searchParams.country === null,
        isUndefined: searchParams.country === undefined,
        stringified: JSON.stringify(searchParams.country)
      },
      city: {
        value: searchParams.city,
        type: typeof searchParams.city,
        isString: typeof searchParams.city === 'string',
        length: searchParams.city?.length,
        isEmpty: searchParams.city === '',
        isNull: searchParams.city === null,
        isUndefined: searchParams.city === undefined,
        stringified: JSON.stringify(searchParams.city)
      }
    });

    // 🚨 CRITICAL FIX: Nettoyer les paramètres vides AVANT l'envoi au backend
    const cleanedParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
      // DEBUGGING: Log chaque décision de nettoyage
      const shouldInclude = value !== null && 
                           value !== undefined && 
                           value !== '' && 
                           !(Array.isArray(value) && value.length === 0);
      
      if (key === 'country' || key === 'city') {
        console.log(`🎯 [DECISION NETTOYAGE] ${key}:`, {
          value: value,
          type: typeof value,
          shouldInclude: shouldInclude,
          reason: !shouldInclude ? 'EXCLUDED - empty/null/undefined' : 'INCLUDED - has value'
        });
      }
      
      if (shouldInclude) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    console.log(`� [CRITICAL FIX] NETTOYAGE DES PARAMÈTRES VIDES:`, {
      BEFORE_CLEANUP: {
        originalParams: searchParams,
        paramCount: Object.keys(searchParams).length,
        country: searchParams.country,
        city: searchParams.city,
        cities: searchParams.cities,
        emptyParams: Object.entries(searchParams).filter(([key, value]) => 
          value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
        ).map(([key, value]) => ({ key, value, type: typeof value }))
      },
      AFTER_CLEANUP: {
        cleanedParams: cleanedParams,
        paramCount: Object.keys(cleanedParams).length,
        country: cleanedParams.country,
        city: cleanedParams.city,
        cities: cleanedParams.cities,
        removedParams: Object.keys(searchParams).filter(key => !(key in cleanedParams))
      },
      SPAIN_BARCELONA_FINAL_STATUS: {
        country_willBeSent: 'country' in cleanedParams ? cleanedParams.country : 'EXCLUDED',
        city_willBeSent: 'city' in cleanedParams ? cleanedParams.city : 'EXCLUDED',
        cities_willBeSent: 'cities' in cleanedParams ? cleanedParams.cities : 'EXCLUDED'
      }
    });
    
    console.log(`🌐 [Frontend → Backend] TRANSMISSION PARAMÈTRES NETTOYÉS:`, {
      url: '/search/intelligent',
      method: 'GET',
      params: cleanedParams,
      originalParamCount: Object.keys(searchParams).length,
      cleanedParamCount: Object.keys(cleanedParams).length,
      serializedQuery: new URLSearchParams(Object.entries(cleanedParams).map(([key, value]) => [key, String(value)])).toString()
    });
    
    const apiStartTime = Date.now();
    
    // 🎯 CORRECTION CRITIQUE: Utiliser les paramètres nettoyés
    const response = await api.get('/search/intelligent', { params: cleanedParams });
    const apiDuration = Date.now() - apiStartTime;
    
    console.log(`📥 [Frontend ← Backend] RÉPONSE API REÇUE:`, {
      apiDuration: `${apiDuration}ms`,
      httpStatus: {
        code: response.status,
        text: response.statusText,
        ok: response.status >= 200 && response.status < 300
      },
      responseStructure: {
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        structure: {
          success: response.data?.success,
          hasData: !!response.data?.data,
          hasPagination: !!response.data?.pagination,
          hasMeta: !!response.data?.meta
        }
      },
      headers: {
        contentType: response.headers['content-type'],
        contentLength: response.headers['content-length']
      }
    });
    
    // Extraire les données de la réponse (structure: {success, data: {listings, count, searchMetadata}, pagination, meta})
    const searchData = response.data.data || response.data;
    
    console.log(`🔍 [IntelligentSearchService:${searchId}] ANALYSE DES DONNÉES REÇUES:`, {
      searchData: {
        dataType: typeof searchData,
        hasListings: !!searchData.listings,
        listingsType: Array.isArray(searchData.listings) ? 'array' : typeof searchData.listings,
        listingsCount: searchData.listings?.length || 0,
        totalCount: searchData.count || searchData.total || 0,
        hasMetadata: !!searchData.searchMetadata,
        metadataKeys: searchData.searchMetadata ? Object.keys(searchData.searchMetadata) : []
      },
      backendResponse: {
        success: response.data?.success,
        message: response.data?.message,
        error: response.data?.error
      }
    });
    
    // Enrichir les résultats sans écraser l'ordre demandé côté backend.
    const enrichedResults = this._enrichResults(searchData, query, {
      sortBy: searchParams.sortBy,
      sortOrder: searchParams.sortOrder
    });
    
    // Ajouter hasMore depuis la pagination du backend
    if (response.data.pagination?.hasMore !== undefined) {
      enrichedResults.hasMore = response.data.pagination.hasMore;
    }
    
    console.log(`✨ [IntelligentSearchService:${searchId}] Results enriched:`, {
      finalResultCount: enrichedResults.listings?.length || 0,
      totalFound: enrichedResults.total || enrichedResults.count || 0,
      hasMore: enrichedResults.hasMore,
      hasMetadata: !!enrichedResults.searchMetadata
    });
    
    // 🆕 MISE EN CACHE ET ENREGISTREMENT INTELLIGENT
    this.searchCache.set(cacheKey, {
      data: enrichedResults,
      timestamp: Date.now()
    });
    
    // 🆕 ENREGISTRER LA NOUVELLE RECHERCHE DANS LE REGISTRE
    this.searchCounter++;
    this.searchRegistry.set(cacheKey, {
      searchNumber: this.searchCounter,
      firstSearched: Date.now(),
      lastAccessed: Date.now(),
      accessCount: 1,
      searchParams: {
        query: query,
        criticalFilters: {
          country: searchParams.country,
          city: searchParams.city,
          cities: searchParams.cities,
          categoryId: searchParams.categoryId
        }
      },
      resultStats: {
        totalFound: enrichedResults.total || enrichedResults.count || 0,
        listingsCount: enrichedResults.listings?.length || 0
      }
    });
    
    console.log(`💾 [IntelligentSearchService:${searchId}] RÉSULTATS CACHÉS ET ENREGISTRÉS:`, {
      cache: {
        cacheKey: cacheKey.substring(0, 50) + '...',
        cacheSize: this.searchCache.size
      },
      registry: {
        searchNumber: this.searchCounter,
        registrySize: this.searchRegistry.size,
        isNewPattern: true,
        futureSearchesWith: 'same filters will use cache'
      }
    });
    
    // 🆕 NETTOYAGE AUTOMATIQUE DU REGISTRE SI NÉCESSAIRE
    this._cleanupRegistry();
    
    // Ajouter à l'historique
    this._addToHistory(query, filters);
    
    const totalDuration = Date.now() - serviceStartTime;
    
    console.log(`🎉 [IntelligentSearchService:${searchId}] RECHERCHE TERMINÉE AVEC SUCCÈS:`, {
      performance: {
        totalDuration: `${totalDuration}ms`,
        apiDuration: `${apiDuration}ms`,
        processingDuration: `${totalDuration - apiDuration}ms`,
        efficiency: `${Math.round((apiDuration / totalDuration) * 100)}% API / ${Math.round(((totalDuration - apiDuration) / totalDuration) * 100)}% processing`
      },
      finalResults: {
        listingsCount: enrichedResults.listings?.length || 0,
        totalFound: enrichedResults.total || enrichedResults.count || 0,
        hasMore: enrichedResults.hasMore,
        hasMetadata: !!enrichedResults.searchMetadata
      },
      cacheStatus: 'newly stored'
    });
    
    console.log(`🏁 ========== [FRONTEND SERVICE:${searchId}] FIN (SUCCÈS) ==========\n`);
    
    return enrichedResults;
    
    } catch (error) {
      const errorDuration = Date.now() - serviceStartTime;
      
      console.error(`❌ [IntelligentSearchService:${searchId}] ERREUR DE RECHERCHE:`, {
        errorDetails: {
          message: error.message,
          stack: error.stack?.split('\n').slice(0, 5).join('\n'),
          name: error.name,
          code: error.code
        },
        httpError: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        } : null,
        requestContext: {
          originalQuery: query,
          originalFilters: filters,
          originalOptions: options,
          userPreferences: userPreferences ? Object.keys(userPreferences) : []
        },
        performance: {
          errorDuration: `${errorDuration}ms`,
          failedAt: 'during API call or processing'
        },
        timestamp: new Date().toISOString()
      });
      
      console.log(`🏁 ========== [FRONTEND SERVICE:${searchId}] FIN (ERREUR) ==========\n`);
      
      throw this._handleSearchError(error);
    }
  }

  /**
   * Recherche géographique simplifiée
   */
  async searchNearby(coordinates, radius = 10, filters = {}, options = {}) {
    try {
      console.log('[IntelligentSearch] Nearby search:', { coordinates, radius, filters });
      
      const searchParams = {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        radius: radius,
        status: 'active',
        ...filters, // Transmission directe des filtres
        limit: options.limit || 20
      };

      const response = await api.get('/search/nearby', { params: searchParams });
      
      // Enrichir avec distance et pertinence géographique
      const enrichedResults = this._enrichNearbyResults(response.data, coordinates);
      
      console.log('[IntelligentSearch] Nearby search completed:', enrichedResults);
      return enrichedResults;
      
    } catch (error) {
      console.error('[IntelligentSearch] Nearby search error:', error);
      throw this._handleSearchError(error);
    }
  }

  /**
   * Suggestions intelligentes avec autocomplétion
   */
  async getSuggestions(partialQuery, userContext = {}) {
    if (!partialQuery || partialQuery.length < 2) {
      return this._getContextualSuggestions(userContext);
    }

    return new Promise((resolve) => {
      this.debouncedSuggestions(partialQuery, userContext, resolve);
    });
  }

  /**
   * Recherche en temps réel avec debouncing simplifiée
   */
  async liveSearch(query, filters = {}, options = {}) {
    return new Promise((resolve, reject) => {
      this.debouncedSearch(query, filters, options, resolve, reject);
    });
  }

  /**
   * Recherche par tendances simplifiée
   */
  async getTrendingSearches(options = {}) {
    try {
      const response = await api.get('/search/trending', {
        params: {
          language: options.language || 'fr',
          location: options.location || null
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('[IntelligentSearch] Trending search error:', error);
      return [];
    }
  }

  /**
   * Recherche par catégories simplifiée
   */
  async searchByCategory(categoryId, options = {}) {
    const filters = {
      categoryId,
      ...options.additionalFilters
    };
    
    return this.searchWithPreferences('', {}, filters, options);
  }

  /**
   * Obtenir l'historique de recherche
   */
  getSearchHistory() {
    return [...this.searchHistory].reverse(); // Plus récent en premier
  }

  /**
   * Nettoyer le cache
   */
  clearCache() {
    this.searchCache.clear();
    this.suggestionCache.clear();
    console.log('[IntelligentSearch] Cache cleared');
  }

  /**
   * Nettoyer l'historique
   */
  clearHistory() {
    this.searchHistory = [];
    console.log('[IntelligentSearch] History cleared');
  }

  // === MÉTHODES PRIVÉES ===

  /**
   * Enrichir les résultats de manière simplifiée
   */
  _enrichResults(results, query, options = {}) {
    if (!results.listings) return results;
    
    const enriched = {
      ...results,
      total: results.count || results.total, // Support pour count (backend) et total (legacy)
      listings: results.listings.map(listing => ({
        ...listing,
        relevanceScore: this._calculateRelevance(listing, query)
      }))
    };
    
    const normalizedSortBy = (options.sortBy || '').toLowerCase();

    // Ne retrier par pertinence que si aucun tri explicite n'est demandé.
    if (!normalizedSortBy || normalizedSortBy === 'relevance') {
      enriched.listings.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));
    }
    
    return enriched;
  }

  /**
   * Enrichir les résultats de recherche géographique
   */
  _enrichNearbyResults(results, coordinates) {
    if (!results.listings) return results;
    
    return {
      ...results,
      listings: results.listings.map(listing => ({
        ...listing,
        distance: this._calculateDistanceFromCoords(
          coordinates,
          { latitude: listing.latitude, longitude: listing.longitude }
        )
      })).sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity))
    };
  }

  /**
   * Calculer la pertinence d'un listing de manière simplifiée
   */
  _calculateRelevance(listing, query) {
    let score = 0;
    
    // Score basé sur le rating
    score += (listing.rating_avg || 0) * 10;
    
    // Score basé sur la correspondance de titre/description
    if (query) {
      const queryLower = query.toLowerCase();
      if (listing.title?.toLowerCase().includes(queryLower)) score += 50;
      if (listing.description?.toLowerCase().includes(queryLower)) score += 25;
    }
    
    // Score basé sur la disponibilité
    if (listing.isAvailable) score += 20;
    
    // Score basé sur la vérification
    if (listing.isVerified) score += 15;
    
    return score;
  }

  /**
   * Calculer la distance entre deux coordonnées
   */
  _calculateDistanceFromCoords(coord1, coord2) {
    if (!coord1.latitude || !coord1.longitude || !coord2.latitude || !coord2.longitude) {
      return null;
    }
    
    const R = 6371; // Rayon de la Terre en km
    const dLat = this._deg2rad(coord2.latitude - coord1.latitude);
    const dLon = this._deg2rad(coord2.longitude - coord1.longitude);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this._deg2rad(coord1.latitude)) * Math.cos(this._deg2rad(coord2.latitude)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return Math.round(distance * 100) / 100; // Arrondir à 2 décimales
  }

  _deg2rad(deg) {
    return deg * (Math.PI/180);
  }

  /**
   * Effectuer la recherche (méthode privée pour debouncing)
   */
  async _performSearch(query, filters, options, resolve, reject) {
    try {
      const results = await this.searchWithPreferences(query, {}, filters, options);
      resolve(results);
    } catch (error) {
      reject(error);
    }
  }

  /**
   * Obtenir les suggestions (méthode privée pour debouncing)
   */
  async _getSuggestions(partialQuery, userContext, resolve) {
    try {
      // Vérifier le cache
      const cacheKey = `suggestions_${partialQuery}_${JSON.stringify(userContext)}`;
      if (this.suggestionCache.has(cacheKey)) {
        const cached = this.suggestionCache.get(cacheKey);
        if (Date.now() - cached.timestamp < this.cacheTimeout) {
          resolve(cached.data);
          return;
        }
      }

      // Utiliser la nouvelle API d'autocomplétion optimisée
      const response = await api.get('/listings/autocomplete', {
        params: {
          q: partialQuery,
          limit: 10
        }
      });

      console.log('[IntelligentSearch] Raw autocomplete response:', response.data);

      // Extraire les suggestions de la nouvelle structure de réponse
      const rawSuggestions = response.data?.suggestions || [];
      
      // Transformer les suggestions pour compatibilité avec l'interface existante
      const transformedSuggestions = rawSuggestions.map(suggestion => ({
        type: suggestion.type,
        text: suggestion.text,
        icon: suggestion.icon,
        description: suggestion.description,
        score: suggestion.score || suggestion.relevanceScore,
        priority: suggestion.priority,
        action: suggestion.action,
        highlighted: suggestion.highlighted || suggestion.text,
        metadata: suggestion.metadata || {}
      }));
      
      // Ajouter suggestions depuis l'historique pour compléter
      const historySuggestions = this._getHistorySuggestions(partialQuery);
      const combinedSuggestions = [
        ...transformedSuggestions,
        ...historySuggestions.slice(0, Math.max(0, 10 - transformedSuggestions.length))
      ].slice(0, 10);
      
      // Mettre en cache
      this.suggestionCache.set(cacheKey, {
        data: combinedSuggestions,
        timestamp: Date.now()
      });
      
      console.log('[IntelligentSearch] Final suggestions:', combinedSuggestions);
      resolve(combinedSuggestions);
      
    } catch (error) {
      console.error('[IntelligentSearch] Suggestions error:', error);
      // Fallback vers les suggestions d'historique en cas d'erreur
      const fallbackSuggestions = this._getHistorySuggestions(partialQuery);
      resolve(fallbackSuggestions);
    }
  }

  /**
   * Obtenir suggestions contextuelles
   */
  _getContextualSuggestions(userContext) {
    const suggestions = [];
    
    // Suggestions basées sur l'historique récent
    const recentSearches = this.searchHistory.slice(-5);
    suggestions.push(...recentSearches.map(search => ({
      type: 'history',
      text: search.query,
      icon: 'history'
    })));
    
    // Suggestions basées sur les préférences
    if (userContext.preferredCategories?.length > 0) {
      suggestions.push(...userContext.preferredCategories.map(category => ({
        type: 'category',
        text: category,
        icon: 'category'
      })));
    }
    
    return suggestions.slice(0, 8);
  }

  /**
   * Obtenir suggestions depuis l'historique
   */
  _getHistorySuggestions(partialQuery) {
    const query = partialQuery.toLowerCase();
    return this.searchHistory
      .filter(search => search.query.toLowerCase().includes(query))
      .slice(0, 3)
      .map(search => ({
        type: 'history',
        text: search.query,
        icon: 'history'
      }));
  }

  /**
   * Ajouter à l'historique de recherche
   */
  _addToHistory(query, filters) {
    if (!query.trim()) return;
    
    // Éviter les doublons
    const existingIndex = this.searchHistory.findIndex(item => item.query === query);
    if (existingIndex !== -1) {
      this.searchHistory.splice(existingIndex, 1);
    }
    
    this.searchHistory.push({
      query,
      filters,
      timestamp: Date.now()
    });
    
    // Limiter la taille de l'historique
    if (this.searchHistory.length > this.maxHistorySize) {
      this.searchHistory.shift();
    }
  }

  /**
   * Générer clé de cache qui tient compte de TOUS les paramètres critiques
   */
  _generateCacheKey(params) {
    console.log(`🔑 [_generateCacheKey] GÉNÉRATION CLÉ DE CACHE:`, {
      inputParams: {
        received: Object.keys(params),
        total: Object.keys(params).length,
        values: params
      }
    });
    
    // ✅ CORRECTION CRITIQUE: Inclure TOUS les paramètres qui affectent les résultats
    const detailsKeyEntries = Object.keys(params)
      .filter(k => k.startsWith('details_'))
      .sort()
      .reduce((acc, k) => {
        const v = params[k];
        if (v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)) {
          acc[k] = Array.isArray(v) ? [...v].sort() : v;
        }
        return acc;
      }, {});

    const keyParams = {
      search: params.search || '',
      country: params.country || '',
      city: params.city || '',
      cities: params.cities || [],
      categoryId: params.categoryId || '',
      categoryIds: params.categoryIds || [], // 🔥 AJOUT CRITIQUE pour support multi-catégories
      isAvailable: params.isAvailable,
      isVerified: params.isVerified,
      minPrice: params.minPrice,
      maxPrice: params.maxPrice,
      rating: params.rating,
      sortBy: params.sortBy || 'relevance',
      sortOrder: params.sortOrder || 'desc',
      limit: params.limit || 10,
      offset: params.offset || 0,
      // 🔥 AJOUT CRITIQUE: Filtres de disponibilité
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      immediateOnly: params.immediateOnly,
      availabilityDuration: params.availabilityDuration,
      // 🔥 AJOUT CRITIQUE: Filtres JSONB details_*
      ...detailsKeyEntries
    };
    
    const cacheKey = JSON.stringify(keyParams);
    
    console.log(`🔑 [_generateCacheKey] CLÉ GÉNÉRÉE:`, {
      keyParams: keyParams,
      keyAnalysis: {
        length: cacheKey.length,
        preview: cacheKey.substring(0, 100) + '...',
        hash: this._hashString(cacheKey),
        includedParams: Object.keys(keyParams).filter(key => 
          keyParams[key] !== null && 
          keyParams[key] !== undefined && 
          keyParams[key] !== '' && 
          !(Array.isArray(keyParams[key]) && keyParams[key].length === 0)
        )
      }
    });
    
    return cacheKey;
  }

  /**
   * Génère un hash simple d'une chaîne pour le debugging
   */
  _hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(16);
  }

  /**
   * Gérer les erreurs de recherche
   */
  _handleSearchError(error) {
    const errorMessage = error.response?.data?.message || error.message || 'Erreur de recherche';
    
    return {
      message: errorMessage,
      type: 'search_error',
      originalError: error
    };
  }

  // ======= NOUVELLES MÉTHODES POUR INTÉGRATION BACKEND V2.0 =======

  /**
   * Obtenir les analytics de recherche en temps réel
   */
  async getRealtimeMetrics() {
    try {
      const response = await api.get('/search/metrics/realtime');
      return response.data;
    } catch (error) {
      console.error('[IntelligentSearch] Error getting realtime metrics:', error);
      return null;
    }
  }

  /**
   * Obtenir les analytics détaillés de recherche
   */
  async getSearchAnalytics(timeframe = '24h') {
    try {
      const response = await api.get('/search/analytics', {
        params: { timeframe }
      });
      return response.data;
    } catch (error) {
      console.error('[IntelligentSearch] Error getting search analytics:', error);
      return null;
    }
  }

  /**
   * Obtenir les statistiques de cache
   */
  async getCacheStats() {
    try {
      const response = await api.get('/search/cache/stats');
      return response.data;
    } catch (error) {
      console.error('[IntelligentSearch] Error getting cache stats:', error);
      return null;
    }
  }

  /**
   * Invalider le cache utilisateur
   */
  async invalidateUserCache(userId) {
    try {
      await api.delete(`/search/cache/user/${userId}`);
      // Aussi nettoyer le cache local
      this.searchCache.clear();
      return true;
    } catch (error) {
      console.error('[IntelligentSearch] Error invalidating user cache:', error);
      return false;
    }
  }

  /**
   * Obtenir la personnalisation utilisateur
   */
  async getUserPersonalization(userId) {
    try {
      const response = await api.get(`/search/personalization/${userId}`);
      return response.data;
    } catch (error) {
      console.error('[IntelligentSearch] Error getting user personalization:', error);
      return null;
    }
  }

  /**
   * Tracker une interaction utilisateur
   */
  async trackInteraction(interactionData) {
    try {
      if (!this.analyticsEnabled) return;
      
      await api.post('/search/interaction', {
        ...interactionData,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        sessionId: this._getSessionId()
      });
      
      // Mettre à jour les métriques locales
      this.performanceMetrics.searchCount++;
      this.performanceMetrics.lastSearchTimestamp = Date.now();
      
    } catch (error) {
      console.error('[IntelligentSearch] Error tracking interaction:', error);
    }
  }

  /**
   * Effectuer une recherche avec cache intelligent simplifiée
   */
  async searchWithCache(query, filters = {}, options = {}) {
    const startTime = Date.now();
    
    try {
      // Utiliser le nouveau endpoint avec cache intégré
      const response = await api.get('/search/intelligent', {
        params: {
          search: query,
          ...filters, // Transmission directe des filtres
          useCache: true
        }
      });
      
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      
      // Tracker les performances
      await this.trackInteraction({
        type: 'search',
        query,
        responseTime,
        resultCount: response.data.results?.length || 0,
        cached: response.data.fromCache || false
      });
      
      return response.data;
      
    } catch (error) {
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      
      // Tracker les erreurs
      await this.trackInteraction({
        type: 'search_error',
        query,
        responseTime,
        error: error.message
      });
      
      throw this._handleSearchError(error);
    }
  }

  /**
   * Obtenir des suggestions personnalisées
   */
  async getPersonalizedSuggestions(query, userId) {
    try {
      const response = await api.get('/search/suggestions/personalized', {
        params: { query, userId }
      });
      return response.data.suggestions || [];
    } catch (error) {
      console.error('[IntelligentSearch] Error getting personalized suggestions:', error);
      return [];
    }
  }

  /**
   * Recherche avec apprentissage automatique simplifiée
   */
  async searchWithLearning(query, filters = {}, options = {}) {
    const result = await this.searchWithCache(query, filters, options);
    
    // Tracker la recherche réussie
    if (result.results && result.results.length > 0) {
      await this.trackInteraction({
        type: 'search_success',
        query,
        filters,
        resultCount: result.results.length,
        topResultCategory: result.results[0]?.category?.name
      });
    }
    
    return result;
  }

  /**
   * Obtenir un ID de session unique
   */
  _getSessionId() {
    if (!this.sessionId) {
      this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return this.sessionId;
  }

  /**
   * Obtenir les métriques de performance locales
   */
  getLocalMetrics() {
    return {
      ...this.performanceMetrics,
      cacheSize: this.searchCache.size,
      historySize: this.searchHistory.length,
      averageResponseTime: this._calculateAverageResponseTime()
    };
  }

  /**
   * Calculer le temps de réponse moyen
   */
  _calculateAverageResponseTime() {
    // Logique simple pour calculer la moyenne
    // Dans une vraie implémentation, on stockerait un historique des temps
    return this.performanceMetrics.averageResponseTime;
  }

  /**
   * Nettoyer le cache local
   */
  clearLocalCache() {
    this.searchCache.clear();
    this.suggestionCache.clear();
    console.log('[IntelligentSearch] Local cache cleared');
  }

  /**
   * Activer/désactiver l'analytics
   */
  setAnalyticsEnabled(enabled) {
    this.analyticsEnabled = enabled;
    console.log(`[IntelligentSearch] Analytics ${enabled ? 'enabled' : 'disabled'}`);
  }
  /**
   * 🆕 NETTOYAGE INTELLIGENT DU REGISTRE
   * Nettoie les anciennes entrées pour éviter la surcharge mémoire
   */
  _cleanupRegistry() {
    const maxRegistrySize = 500; // Limite maximale d'entrées dans le registre
    const cleanupThreshold = 600; // Commencer le nettoyage à ce seuil
    
    if (this.searchRegistry.size >= cleanupThreshold) {
      console.log(`🧹 [IntelligentSearchService] NETTOYAGE DU REGISTRE:`, {
        currentSize: this.searchRegistry.size,
        threshold: cleanupThreshold,
        targetSize: maxRegistrySize
      });
      
      // Convertir en array pour trier par ancienneté
      const entries = Array.from(this.searchRegistry.entries());
      
      // Trier par dernière utilisation (plus anciens en premier)
      entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
      
      // Supprimer les plus anciens pour atteindre la taille cible
      const toDelete = entries.slice(0, entries.length - maxRegistrySize);
      
      toDelete.forEach(([cacheKey, entry]) => {
        this.searchRegistry.delete(cacheKey);
        // Supprimer aussi du cache si présent
        if (this.searchCache.has(cacheKey)) {
          this.searchCache.delete(cacheKey);
        }
      });
      
      console.log(`✅ [IntelligentSearchService] NETTOYAGE TERMINÉ:`, {
        deletedEntries: toDelete.length,
        newRegistrySize: this.searchRegistry.size,
        newCacheSize: this.searchCache.size
      });
    }
  }

  /**
   * 🆕 OBTENIR STATISTIQUES DU REGISTRE
   * Pour le debugging et le monitoring
   */
  getRegistryStats() {
    const entries = Array.from(this.searchRegistry.values());
    
    return {
      registrySize: this.searchRegistry.size,
      cacheSize: this.searchCache.size,
      totalSearches: this.searchCounter,
      oldestEntry: entries.length > 0 ? Math.min(...entries.map(e => e.firstSearched)) : null,
      mostRecentEntry: entries.length > 0 ? Math.max(...entries.map(e => e.lastAccessed)) : null,
      totalAccessCount: entries.reduce((sum, entry) => sum + entry.accessCount, 0),
      averageAccessPerSearch: entries.length > 0 ? 
        entries.reduce((sum, entry) => sum + entry.accessCount, 0) / entries.length : 0
    };
  }

  /**
   * 🆕 FORCER LE NETTOYAGE COMPLET
   * Pour les situations où on veut repartir à zéro
   */
  clearRegistry() {
    console.log(`🗑️ [IntelligentSearchService] NETTOYAGE COMPLET DU REGISTRE:`, {
      registrySize: this.searchRegistry.size,
      cacheSize: this.searchCache.size
    });
    
    this.searchRegistry.clear();
    this.searchCache.clear();
    this.searchCounter = 0;
    
    console.log(`✅ [IntelligentSearchService] REGISTRE NETTOYÉ COMPLÈTEMENT`);
  }
}

// Instance unique du service
const intelligentSearchService = new IntelligentSearchService();

export default intelligentSearchService;